/**
 * Shared process step-edit API — the WYSIWYG step gestures (add / insert /
 * delete / replace-callee / wrap-in-construct / reassign-owner) translated to
 * `mutateProcessStep` model edits, keyed by the walker `NodeId` every process
 * surface already carries. Text stays truth: each gesture renders a step line
 * (chip→rowText, zero drift) and lands as one journaled mutation.
 *
 * Every surface (ProcessTree spine, SwimlaneView flow/sequence, BpmnView)
 * consumes this ONE api so the edit path never forks. Owner reassignment reuses
 * the shipped resolver (`reassignTargets` + `isPlainReassignable`) that the BPMN
 * lane-drag already rides — same gate, no duplicate policy.
 */
import type { NodeId, WalkResult, WalkStep } from "@archlang/engine";
import type { ProcessStepMutation, RemovableArm, WrapConstruct } from "@archlang/engine";
import type { ModelMutationsApi } from "../workbench/mutations.js";
import { isPlainReassignable } from "./bpmnLaneEdit.js";

export type { RemovableArm, WrapConstruct } from "@archlang/engine";

/** A bare / dotted module reference — the only shape authored into a step
 *  operand (the recompile invariant is the last guard). Segments mirror the
 *  LEXER's identifier shape — unicode idents + internal hyphens are legal
 *  module names (spec §5.1); an ASCII-only class silently refused them (r46). */
const REF_SEG = "[A-Za-z_$\\p{ID_Start}][\\w$\\p{ID_Continue}]*(?:-[\\w$\\p{ID_Continue}]+)*";
const REF = new RegExp(`^${REF_SEG}(\\.${REF_SEG})*$`, "u");

export interface ProcessStepEditApi {
  /** Whether any step edit can be issued (a mutation service + process handle
   *  are present) — surfaces gate their affordances on this. */
  readonly canEdit: boolean;
  /** Append a step on its own line after the node. */
  addStepAfter(nodeId: NodeId, text: string): boolean;
  /** Insert a step on its own line before the node. */
  insertBefore(nodeId: NodeId, text: string): boolean;
  /** Prepend a step inside a block node's (possibly empty) body. */
  insertInto(blockNodeId: NodeId, text: string): boolean;
  /** Delete the node (whole compound blocks allowed). */
  deleteStep(nodeId: NodeId): boolean;
  /** Delete SEVERAL nodes as one journaled mutation (multi-select delete, P4
   *  #28). A synchronous `deleteStep` loop can't work — the funnel recompiles
   *  only after the current render, so every call after the first sees the
   *  pre-edit compiled text and refuses `stale`. This resolves all nodeIds up
   *  front and applies the removals against ONE compiled snapshot, so one undo
   *  reverts the whole thing. Unresolvable nodeIds are skipped; returns true
   *  when at least one removal applied. */
  deleteSteps(nodeIds: readonly NodeId[]): boolean;
  /** Inline-edit a terminator's reason/label text (`fail "…"` / `finish "…"`) —
   *  rewrites only the quoted-string span. Refused (false) on a bodyless
   *  terminator (no reason to edit). */
  setTerminatorText(nodeId: NodeId, text: string): boolean;
  /** Inline-edit a branch arm's condition (`if <cond>` / `else if <cond>`) by
   *  arm index (0 = `if`, 1.. = each `else if`). Refused on a body-only draft
   *  branch (no authored condition span). */
  setBranchCondition(nodeId: NodeId, armIndex: number, text: string): boolean;
  /** Inline-edit a `select <head>` head. Refused on a headless select. */
  setSelectHead(nodeId: NodeId, text: string): boolean;
  /** Inline-edit a `select` case label by case index. */
  setCaseLabel(nodeId: NodeId, caseIndex: number, text: string): boolean;
  /** Append an empty `else { }` arm to an `if`. Refused (false) when the `if`
   *  already has an `else`, so it drives an enabled/absent affordance. */
  addElse(nodeId: NodeId): boolean;
  /** Append an empty `catch [Error] { }` arm to a `try`. */
  addCatch(nodeId: NodeId, errorType?: string): boolean;
  /** Remove ONE arm from a branch (`else`/`else if`) or a `catch` from a `try`,
   *  keyed by the enclosing branch/try node + a `RemovableArm` discriminator.
   *  Refused (false) on the primary `if` arm / an out-of-range index — so it
   *  drives an affordance offered only on removable arms. */
  removeArm(nodeId: NodeId, arm: RemovableArm): boolean;
  /** Set / replace / remove the `unwind <text>` compensation on a `reversible`
   *  forward step (keyed by that FORWARD step's nodeId). `text` is the
   *  compensating ACTION (`> Inventory.release` — the saga rule: this step's OWN
   *  compensation); `text===""` removes the unwind. Refused (false) on a step
   *  outside any reversible. */
  setUnwind(nodeId: NodeId, text: string): boolean;
  /** Rewrite a simple `from > to` call (retype / re-callee). */
  replaceCall(nodeId: NodeId, from: string, to: string): boolean;
  /** Whether a step's callee can be inline-retyped safely — i.e. it is a PLAIN
   *  call (no args / comment / `as` / bind / instances), so `replaceStep`'s
   *  whole-`from > to` rewrite drops nothing. Drives an enabled affordance
   *  (double-click to edit), never a silent lossy rewrite. Keyed by stepIndex,
   *  like `canReassignOwner`. */
  canReplaceCall(stepIndex: number): boolean;
  /** Wrap the node — or the contiguous run `nodeId`..`lastNodeId` — in a
   *  control construct. */
  wrap(nodeId: NodeId, construct: WrapConstruct, lastNodeId?: NodeId): boolean;
  /** Flatten a single-body control block in place (remove the wrapper, hoist its
   *  body to the parent level). Refused (false) on the lossy/ambiguous blocks
   *  (an `if` with an `else`, a `select`, a `try` with catches, a multi-branch
   *  `parallel`) — the shared onReject toast surfaces the reason. */
  unwrapBlock(nodeId: NodeId): boolean;
  /** Morph a control block's construct keyword to `to` (e.g. `parallel`↔`each`).
   *  Refused (false) when no byte-clean transform exists — the toast explains. */
  morphBlock(nodeId: NodeId, to: WrapConstruct): boolean;
  /** Set a `parallel`'s merge behaviour + threshold (keyed by the parallel node):
   *  `policy` = `join`/`race`/undefined (bare AND-join), `out` toggles the detach
   *  marker, `count` the `join N`/`race N` threshold (undefined = bare). Refused
   *  (false) on a `parallel await …` (await implies race) and a count over a
   *  named-branch (boolean) merge — the toast explains. */
  setMergeClause(nodeId: NodeId, spec: { policy?: "join" | "race"; out?: boolean; count?: number }): boolean;
  /** Set the inclusive-choice count on a top-level `select` (`all`/`one`/N). */
  setSelectCount(nodeId: NodeId, count: "all" | "one" | number): boolean;
  /** Set the count on the `parallel select` form (`all`/`one`/N). Refused (false)
   *  on a parallel that isn't the `select` form. */
  setParallelSelectCount(nodeId: NodeId, count: "all" | "one" | number): boolean;
  /** Inline-edit the awaited head of a standalone `await` step or a
   *  `parallel await` boundary clause (keyed by either node). */
  setAwaitTarget(nodeId: NodeId, text: string): boolean;
  /** Set / replace / remove one trailing modifier on a CALL step — `kind: "as"`
   *  (the `as <label>` pin alias) or `kind: "comment"` (the freehand `"comment"`),
   *  keyed by the call's nodeId. `text===""` removes it; each modifier lands in its
   *  canonical slot regardless of the other's presence. Refused (false) on a
   *  non-call node (no such slot) and, for `as`, a non-identifier label — so it
   *  drives a presence-gated chip affordance, never a lossy write. */
  setStepModifier(nodeId: NodeId, kind: "as" | "comment", text: string): boolean;
  /** Relocate a step/block before/after a sibling anchor (drag reorder /
   *  reparent-as-sibling). */
  moveStep(nodeId: NodeId, anchorNodeId: NodeId, position: "before" | "after"): boolean;
  /** Relocate SEVERAL steps/blocks (in the given selection order) to sit
   *  contiguously before/after ONE sibling anchor — the multi-select drag path
   *  (P2 #19). ONE journaled mutation (one undo): the engine `moveSteps` op cuts
   *  every span vs one snapshot and re-inserts them as an ordered block, silently
   *  dropping an id nested in another moved block / equal to / enclosing the
   *  anchor / unresolvable (never a torn edit). Returns true when the move
   *  applied. */
  moveSteps(nodeIds: readonly NodeId[], anchorNodeId: NodeId, position: "before" | "after"): boolean;
  /** Reparent a step/block into a block's body (drag drop-into-block). */
  moveStepInto(nodeId: NodeId, blockNodeId: NodeId): boolean;
  /** Reassign a step's OWNER (by walker `stepIndex`) — the same resolver the
   *  BPMN lane-drag uses. Returns false when the step isn't cleanly
   *  reassignable. */
  reassignOwner(stepIndex: number, owner: string): boolean;
  /** Whether a step's owner can be reassigned at all (drives an enabled/disabled
   *  affordance, never a silent no-op). */
  canReassignOwner(stepIndex: number): boolean;
}

const inert: ProcessStepEditApi = {
  canEdit: false,
  addStepAfter: () => false,
  insertBefore: () => false,
  insertInto: () => false,
  deleteStep: () => false,
  deleteSteps: () => false,
  setTerminatorText: () => false,
  setBranchCondition: () => false,
  setSelectHead: () => false,
  setCaseLabel: () => false,
  addElse: () => false,
  addCatch: () => false,
  removeArm: () => false,
  setUnwind: () => false,
  replaceCall: () => false,
  canReplaceCall: () => false,
  wrap: () => false,
  unwrapBlock: () => false,
  morphBlock: () => false,
  setMergeClause: () => false,
  setSelectCount: () => false,
  setParallelSelectCount: () => false,
  setAwaitTarget: () => false,
  setStepModifier: () => false,
  moveStep: () => false,
  moveSteps: () => false,
  moveStepInto: () => false,
  reassignOwner: () => false,
  canReassignOwner: () => false,
};

export function makeProcessStepEditApi(args: {
  readonly mutations: ModelMutationsApi | null;
  /** `mutateProcessStep` target (`#id` or qualified name). */
  readonly processHandle: string | null;
  /** The rendered walk (for owner-reassign gating). */
  readonly walk?: WalkResult | null;
  /** Surface a REJECTED edit to the user (a toast). Called only for a genuine
   *  refusal (`refused` reason / `unknown-target`) — NEVER for `stale`, which is
   *  the invisible mid-typing retry (the generation guard). */
  readonly onReject?: (message: string) => void;
}): ProcessStepEditApi {
  const { mutations, processHandle, walk, onReject } = args;
  if (!mutations || !processHandle) return inert;

  const run = (m: ProcessStepMutation): boolean => {
    const res = mutations.mutateProcessStep(processHandle, m);
    if (res.status === "refused") onReject?.(res.message);
    else if (res.status === "unknown-target") onReject?.("Couldn't locate that step to edit — try again.");
    // `stale` stays silent: the store text is mid-recompile; the caller retries.
    return res.status === "applied";
  };

  const byIndex: ReadonlyMap<number, WalkStep> | undefined = walk?.byIndex;
  const reassignTargets: ReadonlyMap<number, string> | undefined = walk?.reassignTargets;
  const stepKeyByNodeId: ReadonlyMap<NodeId, string> | undefined = walk?.stepKeyByNodeId;
  const reassignTargetFor = (stepIndex: number): string | undefined => {
    if (!byIndex || !reassignTargets) return undefined;
    if (!isPlainReassignable(byIndex.get(stepIndex))) return undefined;
    return reassignTargets.get(stepIndex);
  };
  // Resolve a clicked walk NodeId to its IR node stepId key (the walk NodeId and
  // IR stepId schemes diverge — the walker's map bridges them). A missing entry
  // means the node isn't directly addressable → the op is a no-op.
  const keyOf = (nodeId: NodeId): string | undefined => stepKeyByNodeId?.get(nodeId);

  return {
    canEdit: true,
    addStepAfter: (nodeId, text) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined || text.trim() === "" ? false : run({ op: "insertStepAfter", stepId, text: text.trim() });
    },
    insertBefore: (nodeId, text) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined || text.trim() === "" ? false : run({ op: "insertStepBefore", stepId, text: text.trim() });
    },
    insertInto: (blockNodeId, text) => {
      const blockStepId = keyOf(blockNodeId);
      return blockStepId === undefined || text.trim() === "" ? false : run({ op: "insertStepInto", blockStepId, text: text.trim() });
    },
    deleteStep: (nodeId) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "removeStep", stepId });
    },
    deleteSteps: (nodeIds) => {
      // Resolve every nodeId → stepId against THIS walk (all stable identities of
      // the current compile), then hand the batch to the mutation service which
      // synthesizes them against one compiled snapshot and applies once. A plain
      // `deleteStep` loop would land only the first (the rest refuse `stale`).
      const stepIds = nodeIds
        .map((n) => keyOf(n))
        .filter((s): s is string => s !== undefined);
      if (stepIds.length === 0) return false;
      const res = mutations.mutateProcessSteps(
        processHandle,
        stepIds.map((stepId) => ({ op: "removeStep", stepId }) as const),
      );
      if (res.status === "refused") onReject?.(res.message);
      else if (res.status === "unknown-target") onReject?.("Couldn't locate those steps to delete — try again.");
      return res.status === "applied";
    },
    setTerminatorText: (nodeId, text) => {
      const stepId = keyOf(nodeId);
      // Free-form reason — no trim/REF gate; the engine re-quotes + the recompile
      // invariant is the last guard. A bodyless terminator refuses in the engine.
      return stepId === undefined ? false : run({ op: "setTerminatorText", stepId, text });
    },
    setBranchCondition: (nodeId, armIndex, text) => {
      const stepId = keyOf(nodeId);
      // Free-form condition — the engine picks bare-vs-quoted + refuses a
      // condition-less draft arm. Recompile invariant is the last guard.
      return stepId === undefined ? false : run({ op: "setBranchCondition", stepId, armIndex, text });
    },
    setSelectHead: (nodeId, text) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "setSelectHead", stepId, text });
    },
    setCaseLabel: (nodeId, caseIndex, text) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "setCaseLabel", stepId, caseIndex, text });
    },
    addElse: (nodeId) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "addElse", stepId });
    },
    addCatch: (nodeId, errorType) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "addCatch", stepId, ...(errorType ? { errorType } : {}) });
    },
    removeArm: (nodeId, arm) => {
      const stepId = keyOf(nodeId);
      // The engine refuses the primary `if` arm / out-of-range index — the last
      // guard behind the presence-gated affordance.
      return stepId === undefined ? false : run({ op: "removeArm", stepId, arm });
    },
    setUnwind: (nodeId, text) => {
      const stepId = keyOf(nodeId);
      // Free-form compensating action — the engine renders `unwind <text>` (or
      // removes it when text is empty) and the recompile invariant is the last
      // guard. A non-reversible step refuses in the engine.
      return stepId === undefined ? false : run({ op: "setUnwind", stepId, text });
    },
    replaceCall: (nodeId, from, to) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined || !REF.test(from) || !REF.test(to) ? false : run({ op: "replaceStep", stepId, from, to });
    },
    canReplaceCall: (stepIndex) => {
      const ws = byIndex?.get(stepIndex);
      // Same "plain call" gate the owner-reassign uses — nothing to drop — plus
      // a callee to actually rewrite.
      return isPlainReassignable(ws) && (ws.to?.length ?? 0) > 0;
    },
    wrap: (nodeId, construct, lastNodeId) => {
      const stepId = keyOf(nodeId);
      if (stepId === undefined) return false;
      const lastStepId = lastNodeId ? keyOf(lastNodeId) : undefined;
      if (lastNodeId && lastStepId === undefined) return false;
      return run({ op: "wrapStep", stepId, ...(lastStepId ? { lastStepId } : {}), construct });
    },
    unwrapBlock: (nodeId) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "unwrapBlock", stepId });
    },
    morphBlock: (nodeId, to) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "morphBlock", stepId, to });
    },
    setMergeClause: (nodeId, spec) => {
      const stepId = keyOf(nodeId);
      // The engine refuses a `parallel await …` and a count over a boolean merge —
      // the last guard behind the presence-gated popover.
      return stepId === undefined
        ? false
        : run({
            op: "setMergeClause",
            stepId,
            ...(spec.policy ? { policy: spec.policy } : {}),
            ...(spec.out !== undefined ? { out: spec.out } : {}),
            ...(spec.count !== undefined ? { count: spec.count } : {}),
          });
    },
    setSelectCount: (nodeId, count) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "setSelectCount", stepId, count });
    },
    setParallelSelectCount: (nodeId, count) => {
      const stepId = keyOf(nodeId);
      return stepId === undefined ? false : run({ op: "setParallelSelectCount", stepId, count });
    },
    setAwaitTarget: (nodeId, text) => {
      const stepId = keyOf(nodeId);
      // Free-form head — the engine picks bare-vs-quoted; a non-await node refuses.
      return stepId === undefined ? false : run({ op: "setAwaitTarget", stepId, text });
    },
    setStepModifier: (nodeId, kind, text) => {
      const stepId = keyOf(nodeId);
      // Free-form: the engine quotes a comment / validates an `as` identifier and
      // refuses a non-call node — the last guard behind the presence-gated chips.
      return stepId === undefined ? false : run({ op: "setStepModifier", stepId, kind, text });
    },
    moveStep: (nodeId, anchorNodeId, position) => {
      const stepId = keyOf(nodeId);
      const anchorStepId = keyOf(anchorNodeId);
      return stepId === undefined || anchorStepId === undefined ? false : run({ op: "moveStep", stepId, anchorStepId, position });
    },
    moveSteps: (nodeIds, anchorNodeId, position) => {
      // Resolve every nodeId → stepId against THIS walk; the anchor must resolve
      // (else there's nothing to move relative to). The engine `moveSteps` op is
      // ONE mutation that cuts all spans vs one snapshot and drops the redundant
      // members itself — so this is a single `mutateProcessStep`, one undo, no
      // per-node stale loop. Skips ids that don't map (a synthesized/absent node).
      const anchorStepId = keyOf(anchorNodeId);
      if (anchorStepId === undefined) return false;
      const stepIds = nodeIds
        .map((n) => keyOf(n))
        .filter((s): s is string => s !== undefined && s !== anchorStepId);
      if (stepIds.length === 0) return false;
      return run({ op: "moveSteps", stepIds, anchorStepId, position });
    },
    moveStepInto: (nodeId, blockNodeId) => {
      const stepId = keyOf(nodeId);
      const blockStepId = keyOf(blockNodeId);
      return stepId === undefined || blockStepId === undefined ? false : run({ op: "moveStepInto", stepId, blockStepId });
    },
    reassignOwner: (stepIndex, owner) => {
      if (!REF.test(owner)) return false;
      const stepId = reassignTargetFor(stepIndex);
      if (stepId === undefined) return false;
      return run({ op: "reassignStepOwner", stepId, owner });
    },
    canReassignOwner: (stepIndex) => reassignTargetFor(stepIndex) !== undefined,
  };
}
